import { useState } from 'react';
import { Share2, Check, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';
import { nanoid } from 'nanoid';

interface ShareButtonProps {
  sessionId: string;
  isCreator: boolean;
}

export function ShareButton({ sessionId, isCreator }: ShareButtonProps) {
  const [copySuccess, setCopySuccess] = useState<'share' | 'resume' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation();

  const handleShareLink = async () => {
    try {
      let shareToken;
      
      // Get or create share token
      const { data: sessionData, error: sessionError } = await supabase
        .from('sessions')
        .select('share_token')
        .eq('id', sessionId)
        .single();

      if (sessionError) throw sessionError;

      if (sessionData?.share_token) {
        shareToken = sessionData.share_token;
      } else {
        // Generate and save new token
        shareToken = nanoid(10);
        const { error: updateError } = await supabase
          .from('sessions')
          .update({ share_token: shareToken })
          .eq('id', sessionId);

        if (updateError) throw updateError;
      }

      // Generate and copy share URL
      const shareUrl = new URL(window.location.origin);
      shareUrl.pathname = `/result/${shareToken}`;

      try {
        await navigator.clipboard.writeText(shareUrl.toString());
        setCopySuccess('share');
        setTimeout(() => setCopySuccess(null), 2000);
      } catch (clipboardError) {
        // Fallback: Create a temporary input element
        const tempInput = document.createElement('input');
        tempInput.value = shareUrl.toString();
        document.body.appendChild(tempInput);
        tempInput.select();
        document.execCommand('copy');
        document.body.removeChild(tempInput);
        setCopySuccess('share');
        setTimeout(() => setCopySuccess(null), 2000);
      }
    } catch (err) {
      console.error('Error with share link:', err);
      setError(t('share.error.create'));
      setTimeout(() => setError(null), 3000);
    }
  };

  const handleResumeLink = async () => {
    try {
      const resumeUrl = new URL(window.location.origin);
      resumeUrl.pathname = `/session/${sessionId}`;

      try {
        await navigator.clipboard.writeText(resumeUrl.toString());
        setCopySuccess('resume');
        setTimeout(() => setCopySuccess(null), 2000);
      } catch (clipboardError) {
        // Fallback: Create a temporary input element
        const tempInput = document.createElement('input');
        tempInput.value = resumeUrl.toString();
        document.body.appendChild(tempInput);
        tempInput.select();
        document.execCommand('copy');
        document.body.removeChild(tempInput);
        setCopySuccess('resume');
        setTimeout(() => setCopySuccess(null), 2000);
      }
    } catch (err) {
      console.error('Error copying resume link:', err);
      setError(t('share.error.copy'));
      setTimeout(() => setError(null), 3000);
    }
  };

  if (!isCreator) return null;

  return (
    <div className="flex gap-2">
      {error && (
        <div className="absolute top-0 left-0 right-0 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2 flex items-center gap-2">
          <span className="flex-1">{error}</span>
        </div>
      )}
      
      <button
        onClick={handleShareLink}
        className="inline-flex items-center gap-2 rounded-lg bg-white/80 backdrop-blur-sm px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-white/90 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors"
        aria-label={t('share.result.aria')}
      >
        {copySuccess === 'share' ? (
          <>
            <Check className="h-4 w-4 text-green-500" />
            <span>{t('share.result.copied')}</span>
          </>
        ) : (
          <>
            <Share2 className="h-4 w-4" />
            <span>{t('share.result.copy')}</span>
          </>
        )}
      </button>

      <button
        onClick={handleResumeLink}
        className="inline-flex items-center gap-2 rounded-lg bg-white/80 backdrop-blur-sm px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-white/90 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors"
        aria-label={t('share.resume.aria')}
      >
        {copySuccess === 'resume' ? (
          <>
            <Check className="h-4 w-4 text-green-500" />
            <span>{t('share.resume.copied')}</span>
          </>
        ) : (
          <>
            <ArrowRight className="h-4 w-4" />
            <span>{t('share.resume.copy')}</span>
          </>
        )}
      </button>
    </div>
  );
}